import React, { useState } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;
const TOKEN_KEY = 'admin_token';

/**
 * AIExtractionPanel — read-only display of the AI extraction result
 * stored on a contact / sourcing-request / quote record, plus a
 * "Re-extract" button that re-runs the LLM pass.
 *
 * Props:
 *   kind:        "contact" | "sourcing" | "quote"
 *   recordId:    contact.id / sourcing.id / quote.quote_id
 *   extraction:  pre-loaded extraction object (or null/undefined)
 *   onUpdate:    (newExtraction) => void — called after re-extract
 *   compact:     bool — render in 2-column compact mode instead of 3
 */
export default function AIExtractionPanel({ kind, recordId, extraction, onUpdate, compact = false }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [local, setLocal] = useState(extraction || null);

  React.useEffect(() => { setLocal(extraction || null); }, [extraction]);

  const handleReExtract = async () => {
    if (!recordId) return;
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem(TOKEN_KEY);
      const res = await axios.post(
        `${API}/api/admin/ai/extract`,
        { kind, id: recordId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const next = res.data?.extraction || null;
      setLocal(next);
      if (onUpdate) onUpdate(next);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || 'Failed');
    } finally {
      setLoading(false);
    }
  };

  const status = local?.extraction_status;
  const fields = [
    ['Quantity', local?.quantity],
    ['Brand', local?.brand],
    ['Model', local?.model],
    ['CPU', local?.cpu],
    ['RAM', local?.ram],
    ['Storage', local?.storage],
    ['Form Factor', local?.form_factor],
    ['OS', local?.os],
    ['Licensing', local?.licensing],
    ['Intended Use', local?.intended_use],
    ['Country', local?.country],
    ['Language', local?.language],
  ];
  const populated = fields.filter(([, v]) => v && String(v).trim());

  return (
    <div data-testid={`ai-extraction-panel-${kind}`} className="card-dark border border-white/10 rounded-lg p-4 mb-4">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <span className="text-xs uppercase tracking-widest text-[#00D4FF]">AI Extraction</span>
          {status === 'ok' && (
            <span className="text-[10px] px-2 py-0.5 rounded bg-green-500/15 text-green-300 border border-green-500/30">
              {populated.length}/12 fields
            </span>
          )}
          {status === 'fallback' && (
            <span className="text-[10px] px-2 py-0.5 rounded bg-amber-500/15 text-amber-300 border border-amber-500/30" title="LLM unavailable — regex fallback used">
              Fallback
            </span>
          )}
          {status === 'failed' && (
            <span className="text-[10px] px-2 py-0.5 rounded bg-red-500/15 text-red-300 border border-red-500/30">
              Failed
            </span>
          )}
          {!status && (
            <span className="text-[10px] px-2 py-0.5 rounded bg-white/5 text-white/40 border border-white/10">
              Pending
            </span>
          )}
        </div>
        <button
          type="button"
          onClick={handleReExtract}
          disabled={loading || !recordId}
          data-testid={`ai-reextract-btn-${kind}`}
          className="text-xs px-3 py-1.5 rounded border border-[#00D4FF]/40 text-[#00D4FF] hover:bg-[#00D4FF]/10 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {loading ? 'Re-extracting…' : (local ? 'Re-extract' : 'Run extraction')}
        </button>
      </div>

      {error && (
        <div className="text-xs text-red-300 bg-red-500/10 border border-red-500/30 rounded px-2 py-1 mb-3">
          {error}
        </div>
      )}

      {populated.length === 0 ? (
        <div className="text-xs text-white/40 italic py-2">
          No structured fields extracted yet. Click &quot;Run extraction&quot; to analyse the message with GPT-5.2.
        </div>
      ) : (
        <div className={`grid ${compact ? 'grid-cols-2' : 'grid-cols-2 md:grid-cols-3 lg:grid-cols-4'} gap-x-4 gap-y-2`}>
          {populated.map(([label, value]) => (
            <div key={label} className="text-xs">
              <div className="text-white/40 uppercase tracking-wider text-[10px]">{label}</div>
              <div className="text-white truncate" title={String(value)}>{String(value)}</div>
            </div>
          ))}
        </div>
      )}

      {local?.extracted_at && (
        <div className="mt-3 text-[10px] text-white/30">
          {local.extractor_version || 'extractor'} · {new Date(local.extracted_at).toLocaleString()}
        </div>
      )}
    </div>
  );
}
